import { type I18nFuncs, useI18n } from '@affine/i18n';
import type { ReactElement } from 'react';

import { requestAppResourceJson } from './app-resource-http';
import type { AppResourceKind } from './app-resource-preferences';

export type AppResourceLifecycleOperation =
  | 'move'
  | 'permanent_delete'
  | 'permission_change'
  | 'restore'
  | 'trash';

export type AppResourceLifecycleImpactKind =
  | 'data_source_used_by_app'
  | 'linked_resource_requires_same_area'
  | 'provider_actions_disabled'
  | 'public_route_disabled'
  | 'will_be_read_only'
  | 'will_be_removed_from_apps'
  | 'will_be_restored_in_apps';

export interface AppResourceLifecycleImpact {
  accessDocId: string;
  /** Label of the page data an app reads; set for `data_source_used_by_app`. */
  dataSourceLabel?: string;
  /** The database an app reads; set for `data_source_used_by_app`. */
  databaseId?: string;
  impact: AppResourceLifecycleImpactKind;
  operation: AppResourceLifecycleOperation;
  resourceId: string;
  resourceKind: AppResourceKind;
  title: string;
  workspaceId: string;
}

interface AppResourceLifecycleImpactResponse {
  impacts: AppResourceLifecycleImpact[];
}

export async function fetchDocAppResourceLifecycleImpacts(
  workspaceId: string,
  operation: AppResourceLifecycleOperation,
  docIds: readonly string[],
  targetFolderId?: string | null
): Promise<AppResourceLifecycleImpact[]> {
  const response =
    await requestAppResourceJson<AppResourceLifecycleImpactResponse>(
      appResourceLifecycleImpactUrl(
        workspaceId,
        'docs',
        operation,
        'docId',
        docIds,
        targetFolderId
      )
    );
  return response.impacts;
}

export async function fetchFolderAppResourceLifecycleImpacts(
  workspaceId: string,
  operation: AppResourceLifecycleOperation,
  folderIds: readonly string[],
  targetFolderId?: string | null
): Promise<AppResourceLifecycleImpact[]> {
  const response =
    await requestAppResourceJson<AppResourceLifecycleImpactResponse>(
      appResourceLifecycleImpactUrl(
        workspaceId,
        'folders',
        operation,
        'folderId',
        folderIds,
        targetFolderId
      )
    );
  return response.impacts;
}

/** A pending role change on a page or folder, checked before it is applied. */
export interface AppResourcePermissionChange {
  action: 'grant' | 'revoke' | 'update';
  principal: { id: string; kind: 'group' | 'user' };
  /** Role the principal ends up with; ignored for `revoke`. */
  role?: string | null;
  target: { id: string; kind: 'doc' | 'folder' };
}

/**
 * Lists the apps that read data through the principal and would lose that
 * read access with the change. Revokes, `None`, and root ownership transfers can affect it.
 */
export async function fetchPermissionChangeAppResourceLifecycleImpacts(
  workspaceId: string,
  change: AppResourcePermissionChange
): Promise<AppResourceLifecycleImpact[]> {
  const query = new URLSearchParams({
    action: change.action,
    principalId: change.principal.id,
    principalKind: change.principal.kind,
    targetId: change.target.id,
    targetKind: change.target.kind,
  });
  if (change.role) {
    query.set('role', change.role);
  }
  const response =
    await requestAppResourceJson<AppResourceLifecycleImpactResponse>(
      `${appResourceLifecycleImpactBaseUrl(
        workspaceId
      )}/permission-change?${query.toString()}`
    );
  return response.impacts;
}

export function removesAppResourceReadAccess(
  change: Pick<AppResourcePermissionChange, 'action' | 'role'>
): boolean {
  // Transferring a root doc changes the app's read identity for every source.
  return (
    change.action === 'revoke' ||
    change.role === 'None' ||
    change.role === 'Owner'
  );
}

export function AppResourceLifecycleImpactWarning({
  impacts,
}: {
  impacts: readonly AppResourceLifecycleImpact[];
}): ReactElement | null {
  const t = useI18n();
  if (impacts.length === 0) return null;

  return (
    <section data-testid="app-resource-lifecycle-impact-warning" role="note">
      <ul>
        {impacts.map(impact => (
          <li
            key={`${impact.resourceKind}:${impact.resourceId}:${impact.impact}:${impact.dataSourceLabel ?? ''}`}
          >
            {formatAppResourceLifecycleImpact(impact, t)}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The apps that read one database. A page can host several databases, so a
 * page-wide impact list also names apps of the other ones.
 */
export function appUsagesOfDatabase(
  impacts: readonly AppResourceLifecycleImpact[],
  databaseId: string
): AppResourceLifecycleImpact[] {
  return impacts.filter(
    impact =>
      impact.impact === 'data_source_used_by_app' &&
      impact.databaseId === databaseId
  );
}

export function formatAppResourceLifecycleImpact(
  impact: AppResourceLifecycleImpact,
  t: I18nFuncs
): string {
  if (impact.impact === 'data_source_used_by_app') {
    return formatDataSourceUsedByApp(impact, t);
  }
  const name = `${resourceKindLabel(impact.resourceKind, t)} "${impact.title}"`;
  if (impact.impact === 'linked_resource_requires_same_area') {
    return t[
      'com.streamlain.appResourceLifecycle.linkedResourceRequiresSameArea'
    ]({
      name,
    });
  }
  if (impact.impact === 'provider_actions_disabled') {
    return t['com.streamlain.appResourceLifecycle.providerActionsDisabled']({
      name,
    });
  }
  if (impact.impact === 'public_route_disabled') {
    return t['com.streamlain.appResourceLifecycle.publicRouteDisabled']({
      name,
    });
  }
  if (impact.impact === 'will_be_read_only') {
    return t['com.streamlain.appResourceLifecycle.willBeReadOnly']({ name });
  }
  if (impact.impact === 'will_be_removed_from_apps') {
    return t['com.streamlain.appResourceLifecycle.willBeRemovedFromApps']({
      name,
    });
  }
  return t['com.streamlain.appResourceLifecycle.willBeRestoredInApps']({
    name,
  });
}

function formatDataSourceUsedByApp(
  impact: AppResourceLifecycleImpact,
  t: I18nFuncs
): string {
  // The server withholds the title of apps the actor cannot open.
  const app = impact.title
    ? `${resourceKindLabel(impact.resourceKind, t)} "${impact.title}"`
    : t['com.streamlain.appResourceLifecycle.unknownApp']();
  const data = impact.dataSourceLabel ?? '';
  if (impact.operation === 'move') {
    return t['com.streamlain.appResourceLifecycle.dataSourceUsedByAppMove']({
      app,
      data,
    });
  }
  if (impact.operation === 'permission_change') {
    return t[
      'com.streamlain.appResourceLifecycle.dataSourceUsedByAppPermissionChange'
    ]({
      app,
      data,
    });
  }
  return t['com.streamlain.appResourceLifecycle.dataSourceUsedByAppRemove']({
    app,
    data,
  });
}

function appResourceLifecycleImpactUrl(
  workspaceId: string,
  route: 'docs' | 'folders',
  operation: AppResourceLifecycleOperation,
  idParam: 'docId' | 'folderId',
  ids: readonly string[],
  targetFolderId?: string | null
): string {
  const query = new URLSearchParams({ operation });
  for (const id of ids) {
    query.append(idParam, id);
  }
  if (targetFolderId) {
    query.set('targetFolderId', targetFolderId);
  }
  return `${appResourceLifecycleImpactBaseUrl(
    workspaceId
  )}/${route}?${query.toString()}`;
}

export function appResourceLifecycleImpactBaseUrl(workspaceId: string): string {
  return `/api/workspace/${encodeURIComponent(
    workspaceId
  )}/app-resources/lifecycle-impact`;
}

function resourceKindLabel(
  resourceKind: AppResourceKind,
  t: I18nFuncs
): string {
  if (resourceKind === 'calendar') {
    return t['com.streamlain.appResourceLifecycle.resource.calendar']();
  }
  if (resourceKind === 'mailbox') {
    return t['com.streamlain.appResourceLifecycle.resource.mailbox']();
  }
  if (resourceKind === 'app_base') {
    return t['com.streamlain.appResourceLifecycle.resource.appBase']();
  }
  if (resourceKind === 'kanban') {
    return t['com.streamlain.appResourceLifecycle.resource.kanban']();
  }
  return t['com.streamlain.appResourceLifecycle.resource.default']();
}
